import { 
  Play,
  Users,
  Shield,
  Smartphone,
  BarChart3,
  Globe,
  Zap,
  Settings,
  Camera,
  Headphones,
  TrendingUp,
  Database,
  Lock,
  Eye,
  Wifi,
  Cloud,
  Activity,
  Star,
  MessageSquare,
  Palette,
  Cpu,
  MonitorSpeaker
} from 'lucide-react';

export const streamingPlatformFeatures = {
  keyBenefits: [
    {
      title: 'Multi-Platform Content Delivery',
      description: 'Advanced streaming infrastructure supporting live and on-demand content across web, mobile, smart TV, and gaming consoles with adaptive bitrate streaming.',
      icon: Play,
      metrics: 'Multi-platform support, Adaptive bitrate, Global CDN, 4K/8K streaming',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'Scalable Live Streaming Infrastructure',
      description: 'Enterprise-grade live streaming with ultra-low latency, concurrent viewer support, multi-camera switching, and real-time interaction capabilities.',
      icon: Camera,
      metrics: 'Ultra-low latency, Unlimited viewers, Multi-camera support, Real-time interaction',
      color: 'from-red-500 to-pink-600'
    },
    {
      title: 'Advanced Content Management System',
      description: 'Comprehensive content library with automated transcoding, metadata management, version control, and intelligent content organisation.',
      icon: Database,
      metrics: 'Automated transcoding, Metadata management, Version control, Intelligent organisation',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      title: 'Intelligent Recommendation Engine',
      description: 'AI-powered content recommendations with machine learning algorithms, viewer behaviour analysis, and personalised content discovery.',
      icon: Zap,
      metrics: 'AI recommendations, ML algorithms, Behaviour analysis, Personalised discovery',
      color: 'from-yellow-500 to-orange-600'
    },
    {
      title: 'Comprehensive Analytics & Insights',
      description: 'Advanced streaming analytics with viewer engagement metrics, content performance analysis, revenue tracking, and audience insights.',
      icon: BarChart3,
      metrics: 'Engagement metrics, Performance analysis, Revenue tracking, Audience insights',
      color: 'from-emerald-500 to-green-600'
    },
    {
      title: 'Interactive Viewer Experience',
      description: 'Enhanced viewer engagement with live chat, polls, Q&A sessions, virtual events, and community features for immersive experiences.',
      icon: MessageSquare,
      metrics: 'Live chat, Interactive polls, Q&A sessions, Community features',
      color: 'from-teal-500 to-cyan-600'
    },
    {
      title: 'Monetisation & Revenue Optimisation',
      description: 'Multiple revenue streams with subscription management, pay-per-view, advertising integration, and sponsorship opportunities.',
      icon: TrendingUp,
      metrics: 'Multiple revenue streams, Subscription management, Ad integration, Sponsorship tools',
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Content Security & Protection',
      description: 'Advanced content protection with DRM integration, watermarking, access controls, and piracy prevention measures.',
      icon: Shield,
      metrics: 'DRM integration, Watermarking, Access controls, Piracy prevention',
      color: 'from-red-500 to-pink-600'
    },
    {
      title: 'Mobile-First Streaming Experience',
      description: 'Optimised mobile streaming with offline downloads, data-saving modes, mobile-specific features, and cross-device synchronisation.',
      icon: Smartphone,
      metrics: 'Offline downloads, Data-saving modes, Cross-device sync, Mobile optimisation',
      color: 'from-indigo-500 to-blue-600'
    },
    {
      title: 'Professional Audio Management',
      description: 'High-quality audio streaming with spatial audio support, multi-language tracks, audio descriptions, and professional mixing tools.',
      icon: Headphones,
      metrics: 'Spatial audio, Multi-language tracks, Audio descriptions, Professional mixing',
      color: 'from-violet-500 to-purple-600'
    },
    {
      title: 'Global Content Distribution',
      description: 'Worldwide content delivery with geo-restrictions, localisation support, regional content libraries, and compliance management.',
      icon: Globe,
      metrics: 'Global distribution, Geo-restrictions, Localisation support, Compliance management',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      title: 'Brand Customisation & White-Label',
      description: 'Complete brand customisation with white-label solutions, custom themes, branded players, and personalised user interfaces.',
      icon: Palette,
      metrics: 'White-label solutions, Custom themes, Branded players, Personalised UI',
      color: 'from-pink-500 to-rose-600'
    }
  ],
  
  advancedCapabilities: [
    {
      title: 'AI-Powered Content Creation',
      description: 'Automated content generation with AI editing, highlight reels, thumbnail creation, and intelligent content optimisation for maximum engagement.',
      icon: Cpu,
      metrics: 'AI editing, Automated highlights, Thumbnail generation, Content optimisation',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      title: 'Advanced Video Processing',
      description: 'Professional video processing with real-time effects, colour correction, noise reduction, and automated quality enhancement.',
      icon: Settings,
      metrics: 'Real-time effects, Colour correction, Noise reduction, Quality enhancement',
      color: 'from-orange-500 to-red-600'
    },
    {
      title: 'Interactive Live Features',
      description: 'Enhanced live streaming with virtual backgrounds, augmented reality effects, multi-presenter support, and audience participation tools.',
      icon: Star,
      metrics: 'Virtual backgrounds, AR effects, Multi-presenter support, Audience participation',
      color: 'from-emerald-500 to-green-600'
    },
    {
      title: 'Enterprise Integration Hub',
      description: 'Seamless integration with enterprise systems including CRM, marketing automation, analytics platforms, and third-party services.',
      icon: Settings,
      metrics: 'CRM integration, Marketing automation, Analytics platforms, Third-party services',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      title: 'Advanced Streaming Analytics',
      description: 'Deep analytics with viewer journey mapping, engagement prediction, churn analysis, and content performance optimisation.',
      icon: Activity,
      metrics: 'Journey mapping, Engagement prediction, Churn analysis, Performance optimisation',
      color: 'from-teal-500 to-cyan-600'
    },
    {
      title: 'Multi-Tenant Architecture',
      description: 'Scalable multi-tenant platform supporting multiple brands, isolated content libraries, and customised user experiences.',
      icon: Users,
      metrics: 'Multi-tenant support, Isolated libraries, Brand separation, Customised experiences',
      color: 'from-violet-500 to-purple-600'
    },
    {
      title: 'Professional Broadcasting Tools',
      description: 'Advanced broadcasting capabilities with studio-grade mixing, multi-camera production, graphics overlay, and professional workflows.',
      icon: MonitorSpeaker,
      metrics: 'Studio-grade mixing, Multi-camera production, Graphics overlay, Professional workflows',
      color: 'from-amber-500 to-yellow-600'
    },
    {
      title: 'Edge Computing & CDN Optimisation',
      description: 'Global edge computing with intelligent caching, traffic optimisation, regional servers, and minimal latency delivery.',
      icon: Globe,
      metrics: 'Edge computing, Intelligent caching, Traffic optimisation, Minimal latency',
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Advanced Metadata & Search',
      description: 'Intelligent content discovery with AI-powered tagging, visual recognition, speech-to-text, and advanced search capabilities.',
      icon: Eye,
      metrics: 'AI-powered tagging, Visual recognition, Speech-to-text, Advanced search',
      color: 'from-indigo-500 to-blue-600'
    },
    {
      title: 'Automated Workflow Management',
      description: 'End-to-end workflow automation with content ingestion, processing pipelines, approval workflows, and distribution automation.',
      icon: Zap,
      metrics: 'Content ingestion, Processing pipelines, Approval workflows, Distribution automation',
      color: 'from-yellow-500 to-orange-600'
    },
    {
      title: 'Real-Time Performance Monitoring',
      description: 'Comprehensive monitoring with stream health analytics, viewer experience tracking, technical performance metrics, and proactive alerting.',
      icon: Activity,
      metrics: 'Stream health analytics, Viewer tracking, Performance metrics, Proactive alerting',
      color: 'from-red-500 to-pink-600'
    },
    {
      title: 'Cross-Platform Synchronisation',
      description: 'Seamless cross-device experiences with watch history sync, bookmark sharing, universal search, and unified user profiles.',
      icon: Wifi,
      metrics: 'Cross-device sync, Watch history, Bookmark sharing, Unified profiles',
      color: 'from-cyan-500 to-blue-600'
    }
  ],
  
  securityPerformance: [
    {
      title: 'Enterprise-Grade Security',
      description: 'Military-grade security with end-to-end encryption, secure token authentication, access control management, and comprehensive audit trails.',
      icon: Shield,
      metrics: 'End-to-end encryption, Secure authentication, Access control, Audit trails',
      color: 'from-red-500 to-pink-600'
    },
    {
      title: 'High-Performance Infrastructure',
      description: '99.9% uptime guarantee with global CDN, auto-scaling capabilities, load balancing, and sub-second stream start times.',
      icon: Zap,
      metrics: '99.9% uptime SLA, Global CDN, Auto-scaling, Sub-second start times',
      color: 'from-yellow-500 to-orange-600'
    },
    {
      title: 'Data Protection & Privacy',
      description: 'Comprehensive data protection with GDPR compliance, data encryption, privacy controls, and secure content delivery.',
      icon: Lock,
      metrics: 'GDPR compliance, Data encryption, Privacy controls, Secure delivery',
      color: 'from-gray-500 to-slate-600'
    },
    {
      title: 'Real-Time Monitoring & Support',
      description: 'Continuous system monitoring with proactive alerting, performance optimisation, health checks, and 24/7 technical support.',
      icon: Eye,
      metrics: 'Continuous monitoring, Proactive alerting, Performance optimisation, 24/7 support',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      title: 'Scalable Cloud Architecture',
      description: 'Cloud-native architecture with unlimited scalability, microservices design, containerised deployment, and global distribution.',
      icon: Cloud,
      metrics: 'Unlimited scalability, Microservices design, Containerised deployment, Global distribution',  
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Disaster Recovery & Backup',
      description: 'Comprehensive backup solutions with automated recovery, geographic redundancy, failover systems, and 99.99% data durability.',
      icon: Database,
      metrics: 'Automated recovery, Geographic redundancy, Failover systems, 99.99% data durability',
      color: 'from-blue-500 to-indigo-600'
    }
  ]
};